import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Box, Grid, List, ListItem, Stack, Tab, Tabs } from "@mui/material";
import html2pdf from "html2pdf.js";
import { SwiperSlide } from "swiper/react";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import VSNBreadcrumbs from "../../../shared/VSNBreadcrumbs";
import { PhysicalOnePagerResultStyle } from "./PhysicalOnePagerResultStyle";
import VSNTypography from "../../../shared/VSNTypography";
import { VSNSwiper } from "../../../shared/VSNSwiper";
import VSNPaper from "../../../shared/VSNPaper";
import VSNImage from "../../../shared/VSNImage";
import {
  BoysFive,
  BoysFour,
  BoysOne,
  BoysThree,
  BoysTwo,
  GirlsFive,
  GirlsFour,
  GirlsOne,
  GirlsThree,
  GirlsTwo,
  PurpalFlowerBullet,
  StarIcon,
} from "../../../shared/icon";
import humanBody from "../../../assets/assesments/physical/physical/humanBody.png";
import SwiperCardComponent from "../../assessment/SwiperCardComponent";
import ChartTable, {
  AudioGramTable,
  HeartRateAndBloodPressureTable,
  VisionChartTable,
} from "../../assessment/ChartTable";
import PageStatisticsChart from "../../assessment/PageStatisticsChart";
import { vitalSwiperData } from "./CPETDashboard/AssessmentConstant";
import VSNButton from "../../../shared/VSNButton";
import VSNLoader from "../../../shared/VSNLoader";
import VitalsResultContainer from "../../../container/Vitals.container";
import {
  PDFHeader,
  PDFRecommendationSection,
} from "./PhysicalDashboardPDFCommonComponent";

const PrintComponent = forwardRef((props, ref) => {
  const classes = PhysicalOnePagerResultStyle();
  const { result, header } = props;

  const getBodySvg = (bodyType, waterLevel, index) => {
    const isWaterFilled = waterLevel === "full" || waterLevel === "half";

    const bodySvgMap = {
      body_type_1: isWaterFilled ? (
        <BoysThree key={index} water="220" leval="152" />
      ) : (
        <BoysOne key={index} />
      ),
      body_type_2: isWaterFilled ? (
        <BoysFour key={index} water="220" leval="152" />
      ) : (
        <BoysTwo key={index} />
      ),
      body_type_3: isWaterFilled ? (
        <BoysFour key={index} water="220" leval="152" />
      ) : (
        <BoysThree key={index} />
      ),
      body_type_4: isWaterFilled ? (
        <BoysThree key={index} water="220" leval="152" />
      ) : (
        <BoysFour key={index} />
      ),
      body_type_5: isWaterFilled ? (
        <BoysThree key={index} water="220" leval="152" />
      ) : (
        <BoysFive key={index} />
      ),
      body_type_6: isWaterFilled ? (
        <GirlsThree key={index} water="220" leval="150" />
      ) : (
        <GirlsOne key={index} />
      ),
      body_type_7: isWaterFilled ? (
        <GirlsThree key={index} water="220" leval="150" />
      ) : (
        <GirlsTwo key={index} />
      ),
      body_type_8: isWaterFilled ? (
        <GirlsTwo key={index} water="220" leval="150" />
      ) : (
        <GirlsThree key={index} />
      ),
      body_type_9: isWaterFilled ? (
        <GirlsThree key={index} water="220" leval="150" />
      ) : (
        <GirlsFour key={index} />
      ),
      body_type_10: isWaterFilled ? (
        <GirlsFour key={index} water="220" leval="150" />
      ) : (
        <GirlsFive key={index} />
      ),
    };

    return bodySvgMap[bodyType] || <BoysOne height="400" width="144" />;
  };

  return (
    <Box sx={{ visibility: "hidden", height: 0, overflow: "hidden" }}>
      <Box ref={ref}>
        {result?.body_composition && (
          <Box
            sx={{
              pageBreakAfter: "always",
            }}
          >
            <PDFHeader title="Your Vitals Overview" header={header} />
            <Stack
              className={classes.assessmentReportCard}
              sx={{
                background: "#FAF6F0 !important",
              }}
              id="body"
            >
              <VSNTypography
                variant="h3"
                component="h3"
                className={classes.mdHeadingprimary}
                mb={1}
                sx={{
                  fontSize: "16px !important",
                  color: "#B29263 !important",
                  "&:before": { background: "#B29263 !important" },
                }}
              >
                {result?.body_composition?.title}
              </VSNTypography>
              <VSNTypography component="p" className={classes.desc}>
                {result?.body_composition?.description}
              </VSNTypography>

              <VSNTypography fontSize={16} fontWeight={600} mt={1}>
                Current Composition
              </VSNTypography>

              <Stack direction="row" flexWrap="wrap" mt={1}>
                {result?.body_composition?.current_composition?.map(
                  (item, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      alignItems="center"
                      sx={{ width: "50%", p: 3, backgroundColor: "#fff" }}
                      gap={2}
                    >
                      <Stack
                        key={index}
                        className={classes.pdfBodyCard}
                        position="relative"
                      >
                        {getBodySvg(item?.body_type, item?.water_level, index)}
                        {item?.scale && (
                          <Box position="absolute" sx={{ left: "-15px" }}>
                            <VSNImage src={item?.scale?.image} alt="icon" />
                          </Box>
                        )}
                      </Stack>
                      <Stack justifyContent="center" width="100%" gap={1}>
                        <VSNTypography
                          color="#202024"
                          fontWeight={700}
                          fontSize={16}
                        >
                          {item?.title}
                        </VSNTypography>

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1}
                        >
                          {item?.detail?.map(({ title, value }, index) => (
                            <Stack key={index}>
                              <VSNTypography fontSize={14}>
                                {title}
                              </VSNTypography>
                              <VSNTypography
                                color="#202024"
                                fontWeight={700}
                                fontSize={16}
                              >
                                {value}
                              </VSNTypography>
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    </Stack>
                  )
                )}
              </Stack>

              <Stack>
                <VSNTypography
                  mt={2}
                  fontSize={24}
                  fontWeight={400}
                  sx={{
                    backgroundColor: "var(--white)",
                    padding: { sm: "16px 32px" },
                  }}
                  textAlign="center"
                  borderRadius="8px"
                >
                  Current Body Fat Percentage:{" "}
                  <b>
                    {result?.body_composition?.current_body_fat_percentage ??
                      "-"}{" "}
                    %
                  </b>
                </VSNTypography>
                <VSNTypography
                  mt={2}
                  fontSize={24}
                  fontWeight={400}
                  sx={{
                    backgroundColor: "var(--white)",
                    padding: { sm: "16px 32px" },
                  }}
                  textAlign="center"
                  borderRadius="8px"
                >
                  Current Visceral Fat Percentage:{" "}
                  <b>
                    {result?.body_composition
                      ?.current_visceral_fat_percentage ?? "-"}{" "}
                    %
                  </b>
                </VSNTypography>
              </Stack>
            </Stack>
          </Box>
        )}

        {result?.body_composition && (
          <Box
            sx={{
              pageBreakAfter: "always",
              marginTop: "5px",
            }}
          >
            <Stack
              className={classes.assessmentReportCard}
              sx={{
                background: "#FAF6F0 !important",
              }}
            >
              <VSNTypography fontSize={16} fontWeight={600}>
                History
              </VSNTypography>

              <Stack direction="row" flexWrap="wrap" mt={1}>
                {result?.body_composition?.history
                  ? result?.body_composition?.history?.map((item, index) => (
                      <Stack
                        direction="row"
                        gap="35px"
                        alignItems="center"
                        sx={{ width: "100%", p: 2, backgroundColor: "#fff" }}
                        key={index}
                      >
                        <Stack justifyContent="center" width="100%" gap={3}>
                          <VSNTypography
                            color="#202024"
                            fontWeight={700}
                            fontSize={16}
                          >
                            {item?.year}
                          </VSNTypography>

                          <Stack direction="row" flexWrap="noWrap" gap={2}>
                            {item?.detail?.map(({ title, value }, index) => (
                              <Stack key={index}>
                                <VSNTypography fontSize={14}>
                                  {title}
                                </VSNTypography>
                                <VSNTypography
                                  color="#202024"
                                  fontWeight={700}
                                  fontSize={16}
                                >
                                  {value}
                                </VSNTypography>
                              </Stack>
                            ))}
                          </Stack>
                        </Stack>
                      </Stack>
                    ))
                  : "No History Found"}
              </Stack>
            </Stack>

            <Stack
              className={classes.assessmentReportCard}
              sx={{
                background: "#FAF6F0 !important",
              }}
              my={2}
            >
              <VSNTypography
                className={classes.mdHeadingprimary}
                mb={1}
                sx={{
                  fontSize: "16px !important",
                  color: "#B29263 !important",
                  "&:before": { background: "#B29263 !important" },
                }}
              >
                {
                  result?.body_composition?.body_composition_for_terminology
                    ?.title
                }
              </VSNTypography>
              <VSNTypography component="p" className={classes.desc}>
                {
                  result?.body_composition?.body_composition_for_terminology
                    ?.description
                }
              </VSNTypography>

              <Stack gap={1} pt={1}>
                {result?.body_composition?.body_composition_for_terminology?.body_composition_terms?.map(
                  (el, index) => (
                    <Stack direction="row" key={index}>
                      <Box mr={2} mt={0.6}>
                        {/* <VSNImage
                        src={el?.icon.replace(/\+/g, "%20")}
                        alt="icon"
                        width="14"
                      /> */}
                        <PurpalFlowerBullet height="14" width="14" />
                      </Box>
                      <VSNTypography
                        sx={{
                          fontSize: "14px !important",
                          fontWeight: 500,
                          color: "#0A0A0B",
                          lineHeight: "24px",
                        }}
                      >
                        <span style={{ color: "#86650A", fontWeight: 500 }}>
                          {el?.title ?? ""}{" "}
                        </span>
                        <br />
                        {el?.description}
                      </VSNTypography>
                    </Stack>
                  )
                )}
              </Stack>
            </Stack>
          </Box>
        )}

        {result?.heart_rate_and_blood_pressure && (
          <Box
            sx={{
              pageBreakAfter: "always",
            }}
          >
            {!result?.body_composition && (
              <PDFHeader title="Your Vitals Overview" header={header} />
            )}

            <Stack
              className={classes.assessmentReportCard}
              sx={{
                background: "#FAF6F0 !important",
              }}
              id="heartrate"
            >
              <VSNTypography
                className={classes.mdHeadingprimary}
                mb={1}
                sx={{
                  fontSize: "16px !important",
                  color: "#B29263 !important",
                  "&:before": { background: "#B29263 !important" },
                }}
              >
                {result?.heart_rate_and_blood_pressure?.title}
              </VSNTypography>
              <VSNTypography component="p" className={classes.desc}>
                {result?.heart_rate_and_blood_pressure?.description}
              </VSNTypography>

              <Stack>
                <HeartRateAndBloodPressureTable
                  pdf
                  data={result?.heart_rate_and_blood_pressure?.data}
                />
              </Stack>
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            pageBreakAfter: "always",
            marginTop: "5px",
          }}
        >
          {!result?.body_composition &&
            !result?.heart_rate_and_blood_pressure && (
              <PDFHeader title="Your Vitals Overview" header={header} />
            )}

          <Stack
            className={classes.assessmentReportCard}
            sx={{
              background: "#FAF6F0 !important",
            }}
            id="vision"
          >
            <VSNTypography
              className={classes.mdHeadingprimary}
              mb={1}
              sx={{
                fontSize: "16px !important",
                color: "#B29263 !important",
                "&:before": { background: "#B29263 !important" },
              }}
            >
              {result?.vision_check?.title}
            </VSNTypography>
            <VSNTypography component="p" className={classes.desc}>
              {result?.vision_check?.description}
            </VSNTypography>

            <Stack>
              <ChartTable pdf data={result?.vision_check} />
            </Stack>

            {!result?.vision_check?.glare_sensitivity &&
              !result?.vision_check?.depth_perception && (
                <VisionChartTable
                  data={result?.vision_check?.color_vision}
                  title="Color Perception"
                  name="Number"
                  pdf
                />
              )}
          </Stack>
        </Box>

        {result?.vision_check?.glare_sensitivity &&
          result?.vision_check?.depth_perception && (
            <Box
              sx={{
                pageBreakAfter: "always",
                marginTop: "5px",
              }}
            >
              <Stack
                className={classes.assessmentReportCard}
                sx={{
                  background: "#FAF6F0 !important",
                }}
                id="vision"
              >
                {result?.vision_check?.color_vision && (
                  <VisionChartTable
                    data={result?.vision_check?.color_vision}
                    title="Color Perception"
                    name="Number"
                    pdf
                  />
                )}
                {result?.vision_check?.glare_sensitivity && (
                  <VisionChartTable
                    data={result?.vision_check?.glare_sensitivity}
                    title="Glare Sensitivity"
                    name="Sign"
                    pdf
                  />
                )}
                {result?.vision_check?.depth_perception && (
                  <VisionChartTable
                    data={result?.vision_check?.depth_perception}
                    title="Depth Perception"
                    name="Box Number"
                    pdf
                  />
                )}
              </Stack>
            </Box>
          )}

        <Box
          sx={{
            pageBreakAfter: "always",
            marginTop: "5px",
          }}
        >
          <Stack
            className={classes.assessmentReportCard}
            sx={{
              background: "#FAF6F0 !important",
            }}
            id="hearing"
          >
            <VSNTypography
              className={classes.mdHeadingprimary}
              mb={1}
              sx={{
                fontSize: "16px !important",
                color: "#B29263 !important",
                "&:before": { background: "#B29263 !important" },
              }}
            >
              {result?.hearing_test?.title}
            </VSNTypography>
            <VSNTypography component="p" className={classes.desc}>
              {result?.hearing_test?.description}
            </VSNTypography>

            <Stack gap={2}>
              <Stack
                direction="row"
                gap={2}
                sx={{ backgroundColor: "#fff", p: 1, mt: 2 }}
              >
                <PageStatisticsChart
                  name="Right"
                  lineColor="#F54346"
                  height="270px"
                  width="330px"
                  data={result?.hearing_test?.right?.audiogram ?? []}
                />
                <PageStatisticsChart
                  name="Left"
                  lineColor="#34A259"
                  height="270px"
                  width="330px"
                  data={result?.hearing_test?.left?.audiogram ?? []}
                />
              </Stack>

              <AudioGramTable
                height="270px"
                data={result?.hearing_test?.right}
              />

              <AudioGramTable
                height="270px"
                color
                data={result?.hearing_test?.left}
              />
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            pageBreakAfter: "always",
            marginTop: "5px",
          }}
        >
          {/* <Stack
            className={classes.assessmentReportCard}
            sx={{
              background: "#FAF6F0 !important",
            }}
            mb={2}
          >
            <VSNTypography
              className={classes.mdHeadingprimary}
              sx={{
                fontSize: "16px !important",
                backgroundColor: "pink !importnant",
                color: "#B29263 !important",
                "&:before": { background: "#B29263 !important" },
              }}
            >
              {result?.clinician_recommendations?.title}
            </VSNTypography>
            <VSNTypography component="p" className={classes.desc}>
              {result?.clinician_recommendations?.description}
            </VSNTypography>
          </Stack> */}

          <PDFRecommendationSection
            data={result?.recommendations ?? {}}
            staticData={vitalSwiperData ?? []}
          />
        </Box>
      </Box>
    </Box>
  );
});

const BodyConsumption = ({ data }) => {
  const classes = PhysicalOnePagerResultStyle();
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const getBodySvg = (bodyType, waterLevel, index) => {
    const isWaterFilled = waterLevel === "full" || waterLevel === "half";

    const bodySvgMap = {
      body_type_1: isWaterFilled ? (
        <BoysThree key={index} water="220" leval="152" />
      ) : (
        <BoysOne key={index} />
      ),
      body_type_2: isWaterFilled ? (
        <BoysFour key={index} water="220" leval="152" />
      ) : (
        <BoysTwo key={index} />
      ),
      body_type_3: isWaterFilled ? (
        <BoysFour key={index} water="220" leval="152" />
      ) : (
        <BoysThree key={index} />
      ),
      body_type_4: isWaterFilled ? (
        <BoysThree key={index} water="220" leval="152" />
      ) : (
        <BoysFour key={index} />
      ),
      body_type_5: isWaterFilled ? (
        <BoysThree key={index} water="220" leval="152" />
      ) : (
        <BoysFive key={index} />
      ),
      body_type_6: isWaterFilled ? (
        <GirlsThree key={index} water="220" leval="150" />
      ) : (
        <GirlsOne key={index} />
      ),
      body_type_7: isWaterFilled ? (
        <GirlsThree key={index} water="220" leval="150" />
      ) : (
        <GirlsTwo key={index} />
      ),
      body_type_8: isWaterFilled ? (
        <GirlsTwo key={index} water="220" leval="150" />
      ) : (
        <GirlsThree key={index} />
      ),
      body_type_9: isWaterFilled ? (
        <GirlsThree key={index} water="220" leval="150" />
      ) : (
        <GirlsFour key={index} />
      ),
      body_type_10: isWaterFilled ? (
        <GirlsFour key={index} water="220" leval="150" />
      ) : (
        <GirlsFive key={index} />
      ),
    };

    return bodySvgMap[bodyType] || <BoysOne height="400" width="144" />;
  };

  return (
    <Grid item xl={6} lg={6} xs={12}>
      <Stack className={classes.assessmentReportCard}>
        <VSNTypography
          variant="h3"
          component="h3"
          className={classes.mdHeadingprimary}
          mb={1}
          sx={{ fontSize: "20px" }}
        >
          {data?.title}
        </VSNTypography>
        <VSNTypography
          component="p"
          className={classes.desc}
          fontSize={{ xsm: 16, sm: 16 }}
          fontWeight={500}
        >
          {data?.description}
        </VSNTypography>

        <Grid container mt={5} spacing={2.5}>
          <Grid item xl={6} lg={6} xs={12}>
            <Tabs
              className={classes.swiperCustomTabs}
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab disableRipple label="Current Composition" />
              <Tab disableRipple label="History" />
            </Tabs>

            {value === 0 ? (
              <>
                {data?.current_body_fat_percentage ? (
                  <Box position="relative">
                    <Stack
                      direction="row"
                      spacing={1.4}
                      className={`${classes.VitalcustomArrow} ${classes.customArrow}`}
                    >
                      <div className="swiper-button image-swiper-button-next" />
                      <div className="swiper-button image-swiper-button-prev" />
                    </Stack>
                    <VSNSwiper
                      className={classes.containerCard}
                      navigation={{
                        prevEl: ".image-swiper-button-next",
                        nextEl: ".image-swiper-button-prev",
                        disabledClass: "swiper-button-disabled",
                      }}
                      breakpoints={{
                        0: {
                          slidesPerView: 1,
                          spaceBetween: 20,
                        },
                      }}
                    >
                      {data?.current_composition?.map((item, index) => (
                        <SwiperSlide key={index}>
                          <VSNPaper
                            sx={{
                              borderRadius: "12px",
                              textAlign: "start",
                              border: "none",
                            }}
                            className={classes.mainWrapper}
                          >
                            <Stack
                              direction={{ sm: "row", xs: "column" }}
                              gap={{ md: "45px", xs: "24px" }}
                              alignItems="center"
                              className="inner"
                            >
                              <Stack
                                key={index}
                                className={classes.bodyCard}
                                position="relative"
                              >
                                {getBodySvg(
                                  item?.body_type,
                                  item?.water_level,
                                  index
                                )}

                                {item?.scale && (
                                  <Box
                                    position="absolute"
                                    sx={{ left: "-20px" }}
                                  >
                                    <VSNImage
                                      src={item?.scale?.image}
                                      alt="icon"
                                    />
                                  </Box>
                                )}
                              </Stack>
                              <Stack
                                justifyContent="center"
                                width="100%"
                                gap={3}
                              >
                                <VSNTypography
                                  variant="subtitle1"
                                  component="h6"
                                  color="#202024"
                                  fontWeight={700}
                                  fontSize={{ xs: 18, md: 24 }}
                                >
                                  {item?.title}
                                </VSNTypography>

                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  flexWrap="wrap"
                                  gap={{ xl: 5, xs: 2 }}
                                >
                                  {item?.detail?.map(
                                    ({ title, value }, index) => (
                                      <Stack
                                        key={index}
                                        className={classes.singleValueBox}
                                      >
                                        <VSNTypography component="p">
                                          {title}
                                        </VSNTypography>
                                        <VSNTypography
                                          variant="subtitle1"
                                          color="#202024"
                                          fontWeight={700}
                                          fontSize={{ xs: 26, xl: 34 }}
                                        >
                                          {value}
                                        </VSNTypography>
                                      </Stack>
                                    )
                                  )}
                                </Stack>
                              </Stack>
                            </Stack>
                          </VSNPaper>
                        </SwiperSlide>
                      ))}
                    </VSNSwiper>
                  </Box>
                ) : (
                  <Box
                    p={6}
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      backgroundColor: "var(--white)",
                      borderRadius: "16px",
                    }}
                  >
                    No History Found
                  </Box>
                )}
              </>
            ) : (
              <>
                {data?.history?.length > 0 ? (
                  <Box position="relative">
                    <Stack
                      direction="row"
                      spacing={1.4}
                      className={`${classes.VitalcustomArrow} ${classes.customArrow}`}
                    >
                      <div className="swiper-button image-swiper-button-next" />
                      <div className="swiper-button image-swiper-button-prev" />
                    </Stack>
                    <VSNSwiper
                      className={classes.containerCard}
                      navigation={{
                        prevEl: ".image-swiper-button-next",
                        nextEl: ".image-swiper-button-prev",
                        disabledClass: "swiper-button-disabled",
                      }}
                      breakpoints={{
                        0: {
                          slidesPerView: 1,
                          spaceBetween: 20,
                        },
                      }}
                    >
                      {data?.history?.map((item, index) => (
                        <SwiperSlide key={index}>
                          <VSNPaper
                            sx={{
                              borderRadius: "12px",
                              textAlign: "start",
                              border: "none",
                            }}
                            className={classes.mainWrapper}
                          >
                            <Stack
                              direction="row"
                              gap="45px"
                              alignItems="center"
                            >
                              <Stack
                                justifyContent="center"
                                width="100%"
                                gap={3}
                              >
                                <VSNTypography
                                  variant="subtitle1"
                                  component="h6"
                                  color="#202024"
                                  fontWeight={700}
                                  fontSize={{ xs: 18, md: 24 }}
                                >
                                  {item?.year}
                                </VSNTypography>

                                <Stack
                                  direction="row"
                                  flexWrap="wrap"
                                  gap={{ md: 5, xs: 3 }}
                                >
                                  {item?.detail?.map(
                                    ({ title, value }, index) => (
                                      <Stack
                                        key={index}
                                        className={`${classes.singleValueBox} history`}
                                      >
                                        <VSNTypography
                                          variant="subtitle1"
                                          fontSize={{ xs: 14, md: 18 }}
                                          color="#898994"
                                          fontWeight={600}
                                        >
                                          {title}
                                        </VSNTypography>
                                        <VSNTypography
                                          component="h5"
                                          color="#202024"
                                          fontWeight={700}
                                          fontSize={{ xs: 26, md: 34 }}
                                        >
                                          {value}
                                        </VSNTypography>
                                      </Stack>
                                    )
                                  )}
                                </Stack>
                              </Stack>
                            </Stack>
                          </VSNPaper>
                        </SwiperSlide>
                      ))}
                    </VSNSwiper>
                  </Box>
                ) : (
                  <Box
                    p={6}
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      backgroundColor: "var(--white)",
                      borderRadius: "16px",
                    }}
                  >
                    No History Found
                  </Box>
                )}
              </>
            )}
          </Grid>

          <Grid item xl={6} lg={6} xs={12}>
            <Stack className={classes.assessmentReportCardWhite}>
              <VSNTypography
                variant="h3"
                component="h3"
                className={classes.mdHeading}
                mb={1}
              >
                {data?.body_composition_for_terminology?.title ?? ""}
              </VSNTypography>
              <VSNTypography component="p" className={classes.desc}>
                {data?.body_composition_for_terminology?.description ?? ""}
              </VSNTypography>
              <List
                className={`${classes.dailyFunctioning} ${classes.vitalList}`}
              >
                {data?.body_composition_for_terminology?.body_composition_terms?.map(
                  (el, index) => (
                    <ListItem key={index}>
                      {/* <Box mt={0.5}>
                        <PurpalFlowerBullet />
                      </Box> */}

                      <Box mt={0.5}>
                        <VSNImage src={el?.icon} alt="icon" />
                      </Box>
                      <VSNTypography color="var(--blackGray)">
                        <span style={{ color: "#86650A", fontWeight: 500 }}>
                          {el?.title ?? ""}{" "}
                        </span>
                        <br />
                        {el?.description}
                      </VSNTypography>
                    </ListItem>
                  )
                )}
              </List>
            </Stack>
          </Grid>
        </Grid>
        <Stack direction={{ md: "coulmn", lg: "row" }} gap={2}>
          <VSNTypography
            mt={{ md: "32px", xs: 3 }}
            component="h1"
            variant="h3"
            fontSize={{ xs: 24, md: 34 }}
            fontWeight={400}
            sx={{
              backgroundColor: "var(--white)",
              padding: { sm: "16px 32px", xs: "16px" },
              width: "100%",
            }}
            textAlign="center"
            borderRadius="8px"
          >
            Current Body Fat Percentage:{" "}
            <b>{data?.current_body_fat_percentage ?? 0} %</b>
          </VSNTypography>
          <VSNTypography
            mt={{ md: "32px", xs: 3 }}
            component="h1"
            variant="h3"
            fontSize={{ xs: 24, md: 34 }}
            fontWeight={400}
            sx={{
              backgroundColor: "var(--white)",
              padding: { sm: "16px 32px", xs: "16px" },
              width: "100%",
            }}
            textAlign="center"
            borderRadius="8px"
          >
            Current Visceral Fat Percentage:{" "}
            <b>{data?.current_visceral_fat_percentage ?? 0} %</b>
          </VSNTypography>
        </Stack>
      </Stack>
    </Grid>
  );
};
const VisionCheck = ({ data }) => {
  const classes = PhysicalOnePagerResultStyle();

  return (
    <Grid item md={12} xs={12}>
      <Stack className={classes.assessmentReportCard}>
        <VSNTypography
          variant="h3"
          component="h3"
          className={classes.mdHeadingprimary}
          mb={1}
          sx={{ fontSize: "20px" }}
        >
          {data?.title}
        </VSNTypography>
        <VSNTypography
          component="p"
          className={classes.desc}
          fontSize={{ xsm: 16, sm: 16 }}
          fontWeight={500}
        >
          {data?.description}
        </VSNTypography>

        <ChartTable data={data} />
        {data?.color_vision && (
          <VisionChartTable
            data={data?.color_vision}
            title="Color Perception"
            name="Number"
          />
        )}
        {data?.glare_sensitivity && (
          <VisionChartTable
            data={data?.glare_sensitivity}
            title="Glare Sensitivity"
            name="Sign"
          />
        )}
        {data?.depth_perception && (
          <VisionChartTable
            data={data?.depth_perception}
            title="Depth Perception"
            name="Box Number"
          />
        )}
      </Stack>
    </Grid>
  );
};
const HeartRateAndBloodPressure = ({ data }) => {
  const classes = PhysicalOnePagerResultStyle();

  return (
    <Grid item md={12} xs={12}>
      <Stack className={classes.assessmentReportCard}>
        <VSNTypography
          variant="h3"
          component="h3"
          className={classes.mdHeadingprimary}
          mb={1}
          sx={{ fontSize: "20px" }}
        >
          {data?.title}
        </VSNTypography>
        <VSNTypography
          component="p"
          className={classes.desc}
          fontSize={{ xsm: 16, sm: 16 }}
          fontWeight={500}
        >
          {data?.description}
        </VSNTypography>

        <HeartRateAndBloodPressureTable data={data?.data} />
      </Stack>
    </Grid>
  );
};
const HearingTest = ({ data }) => {
  const classes = PhysicalOnePagerResultStyle();
  return (
    <Grid item md={12} xs={12}>
      <Stack className={classes.assessmentReportCard}>
        <VSNTypography
          variant="h3"
          component="h3"
          className={classes.mdHeadingprimary}
          mb={1}
          sx={{ fontSize: "20px" }}
        >
          {data?.title ?? ""}
        </VSNTypography>
        <VSNTypography
          component="p"
          className={classes.desc}
          fontSize={{ xsm: 16, sm: 16 }}
          fontWeight={500}
        >
          {data?.description ?? ""}
        </VSNTypography>

        <Grid container spacing={4} py={2}>
          <Grid item xl={6} lg={6} xs={12}>
            <PageStatisticsChart
              name="Left"
              lineColor="#F54346"
              height="350px"
              width="100%"
              data={data?.left?.current ?? []}
            />
          </Grid>
          <Grid item xl={6} lg={6} xs={12}>
            <PageStatisticsChart
              name="Right"
              lineColor="#34A259"
              height="350px"
              width="100%"
              data={data?.right?.current ?? []}
            />
          </Grid>
          <Grid item xl={6} lg={6} xs={12}>
            <AudioGramTable height="100%" data={data?.left} />
          </Grid>
          <Grid item xl={6} lg={6} xs={12}>
            <AudioGramTable height="100%" color data={data?.right} />
          </Grid>
        </Grid>
      </Stack>
    </Grid>
  );
};

const VitalsResult = ({
  endPointToCall,
  showBreadCrumbs = true,
  breadCrumbAndButton,
  releaseButton,
  assessmentUserInfo,
  viewAllResult,
  from,
}) => {
  const classes = PhysicalOnePagerResultStyle();

  const {
    result: containerResult,
    loader,
    currentAssessment,
  } = from === "viewAll" ? {} : VitalsResultContainer({ endPointToCall });

  const result = from === "viewAll" ? viewAllResult : containerResult;
  const [showPrintComponent, setShowPrintComponent] = useState(false);
  const printRef = useRef();
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(0);

  const availableTabs = [];
  if (result?.body_composition)
    availableTabs.push({ label: "Body Composition", key: "body_composition" });
  if (result?.heart_rate_and_blood_pressure)
    availableTabs.push({
      label: "Heart Rate & Blood Pressure",
      key: "heart_rate_and_blood_pressure",
    });
  if (result?.vision_check)
    availableTabs.push({ label: "Vision Check", key: "vision_check" });
  if (result?.hearing_test)
    availableTabs.push({ label: "Hearing Test", key: "hearing_test" });

  const [currentData, setCurrentData] = useState(
    availableTabs.length > 0 ? result?.[availableTabs[0].key] : null
  );

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setCurrentData(result?.[availableTabs[newValue].key] || {});
  };

  const PDFDetails = assessmentUserInfo ?? result?.info;

  useEffect(() => {
    if (result && availableTabs?.length > 0) {
      const firstAvailableKey = availableTabs?.find(
        (tab) => result?.[tab.key]
      )?.key;
      setCurrentData(result?.[firstAvailableKey] || null);
    }
  }, [result, availableTabs.length]);

  const breadcrumbsRoutes = [
    {
      path: "/assessments",
      label: "Assessments",
    },
    {
      path: `/assessments/${currentAssessment?.assessment_id}`,
      label: `${currentAssessment?.assessment_name} Dashboard`,
    },
  ];

  const handleDownloadPDF = () => {
    setLoading(true);
    setShowPrintComponent(true);

    setTimeout(() => {
      const element = printRef.current;

      const adminFilename = `${assessmentUserInfo?.name}_${currentAssessment?.assessment_name}.pdf`;
      const userFilename = `${result?.info?.name}_${currentAssessment?.assessment_name}.pdf`;
      const fileName = assessmentUserInfo ? adminFilename : userFilename;

      const options = {
        margin: 6,
        filename: fileName,
        image: { type: "jpeg", quality: 0.7 },
        html2canvas: { scale: 1.7, useCORS: true, allowTaint: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      html2pdf()
        .from(element)
        .set(options)
        .toPdf()
        .get("pdf")
        .then((pdf) => {
          pdf.save(fileName);
        })
        .finally(() => {
          setShowPrintComponent(false);
          setLoading(false);
        });
    }, 1000);
  };

  if (from !== "viewAll" && (loader?.assessmentResult || !currentData)) {
    return <VSNLoader variant="fullPage" />;
  }

  return (
    <div className={classes.mainLayout}>
      {from !== "viewAll" ? (
        showBreadCrumbs ? (
          <Box mt={2.5} mb={1.8}>
            <VSNBreadcrumbs {...{ breadcrumbsRoutes }} />
          </Box>
        ) : (
          <Box className={classes.headingButton}>
            <Box>{breadCrumbAndButton}</Box>
            <Stack
              direction="row"
              gap={{ sm: 2, xs: 1 }}
              alignItems="center"
              flexWrap="wrap"
              sx={{ width: { xsm: "auto", xs: "100%" } }}
            >
              <VSNButton
                className={classes.PrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print Vital
                </Stack>
              </VSNButton>
              {currentData?.is_locked ? releaseButton : ""}
            </Stack>
          </Box>
        )
      ) : null}

      {result?.message === "No report found" ? (
        <Stack direction="row" justifyContent="center" p={5}>
          <VSNTypography variant="h5">No Report Found</VSNTypography>
        </Stack>
      ) : (
        <>
          {showBreadCrumbs && (
            <Stack
              direction={{ xsm: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <VSNTypography
                fontSize={{ xsm: 24, sm: 34 }}
                sx={{ fontWeight: 700 }}
              >
                {result?.title ?? ""}
              </VSNTypography>
              <VSNButton
                className={classes.PrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print Vital Report
                </Stack>
              </VSNButton>
            </Stack>
          )}
          {showPrintComponent && (
            <PrintComponent
              ref={printRef}
              result={result}
              header={PDFDetails}
            />
          )}

          <Box sx={{ width: "100%" }} pb="24px">
            <Tabs
              className={classes.CustomTabs}
              value={value}
              onChange={handleChange}
            >
              {availableTabs?.map((tab, index) => (
                <Tab key={index} disableRipple label={tab.label} />
              ))}
            </Tabs>

            {availableTabs?.length > 0 && value < availableTabs?.length && (
              <>
                {availableTabs[value]?.key === "body_composition" && (
                  <BodyConsumption data={currentData} />
                )}
                {availableTabs[value]?.key ===
                  "heart_rate_and_blood_pressure" && (
                  <HeartRateAndBloodPressure data={currentData} />
                )}

                {availableTabs[value]?.key === "vision_check" && (
                  <VisionCheck data={currentData} />
                )}
                {availableTabs[value]?.key === "hearing_test" && (
                  <HearingTest data={currentData} />
                )}
              </>
            )}
          </Box>

          {availableTabs?.length > 1 && (
            <SwiperCardComponent
              title={result?.recommendations?.title}
              description={result?.recommendations?.description}
              data={result?.recommendations?.recommendations ?? []}
            />
          )}
        </>
      )}
    </div>
  );
};

export default VitalsResult;
